import { useEffect, useRef } from 'react';
import type { SurveyQuestion } from '../types.js';

// Question types that get auto-advance: any single-pick scale or choice where
// the value the user lands on is unambiguously their final answer. Multi-choice
// (checkboxes) is intentionally excluded — the user might still be adding picks.
//
// Exported so surfaces can mirror the same eligibility check when deciding
// whether to show pending-advance hints (e.g. ConversationalSurvey).
export const AUTO_ADVANCE_TYPES = new Set([
  'nps',
  'csat',
  'ces',
  'likert',
  'scale',
  'singleChoice',
  'choice',
  'yesNo',
  'emojiScale',
  'dropdown',
  // Stars/rating: included so conversational mode advances after picking,
  // matching NPS/CSAT behaviour. The 600 ms delay gives respondents who
  // hover-then-decide enough time to change their mind before the page
  // slides away.
  'rating',
]);

// 400 ms is long enough to register the choice visually before the page
// slides, short enough that confident respondents don't feel held up.
const DEFAULT_DELAY_MS = 400;
// Stars need a longer hold because respondents tend to scrub across the
// row before committing — a 400 ms delay would fire mid-scrub.
const RATING_DELAY_MS = 600;

interface UseAutoAdvanceParams {
  question: SurveyQuestion | null | undefined;
  value: unknown;
  enabled: boolean;
  // Called once per meaningful value change after `delayMs`. Cancels and
  // re-arms if value changes again before the delay elapses.
  onAdvance: () => void;
  delayMs?: number;
}

export function useAutoAdvance({ question, value, enabled, onAdvance, delayMs }: UseAutoAdvanceParams): void {
  // Track the value we already auto-advanced from so re-renders with the same
  // value don't re-fire (e.g. parent state updates that don't change `value`).
  const lastTriggerRef = useRef<unknown>(undefined);
  // Track which question this hook last saw so we can detect navigation
  // between questions (forward via auto-advance, backward via Back button,
  // jump via path logic) and prime the trigger ref against the value that
  // is already there. Without this, navigating Back to an already-answered
  // question fires auto-advance forward again — the same answer is still
  // "meaningful" and unequal to whatever was last advanced from.
  const lastQuestionKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    if (!question) return;
    if (!AUTO_ADVANCE_TYPES.has(question.type)) return;

    // First time we see this question — treat the incoming value as the
    // baseline so re-entry (Back / forward chevron / path jump) never
    // auto-advances forward. The user must change the value to trigger
    // another advance.
    if (lastQuestionKeyRef.current !== question.key) {
      lastQuestionKeyRef.current = question.key;
      lastTriggerRef.current = value;
      return;
    }

    if (!isMeaningful(value)) {
      lastTriggerRef.current = undefined;
      return;
    }
    if (lastTriggerRef.current === value) return;

    const resolvedDelay = delayMs ?? (question.type === 'rating' ? RATING_DELAY_MS : DEFAULT_DELAY_MS);
    const handle = setTimeout(() => {
      lastTriggerRef.current = value;
      onAdvance();
    }, resolvedDelay);
    return () => clearTimeout(handle);
  }, [question, value, enabled, onAdvance, delayMs]);
}

function isMeaningful(value: unknown): boolean {
  if (value === undefined || value === null) return false;
  if (typeof value === 'string' && value.length === 0) return false;
  if (Array.isArray(value) && value.length === 0) return false;
  return true;
}
